import React, { useEffect, useState, useRef } from "react";
import { can as canReports, canImport } from '../permissions/access';
import { Link, useHistory, useLocation } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import { productAction } from "../../_actions/product.action";
import CatalogueTopNav from "../new-service/CatalogueTopNav";
import ProductSubNav from "./ProductSubNav";
import ProductData from "./ProductData";
import ProductHelp from "./ProductHelp";
import ProductOrders from "./ProductOrders";
import ProductStoreSettings from "./ProductStoreSettings";
import { getOrders } from "./productCommerce";
import '../forms/Forms.css';
import { RxCross2 } from "react-icons/rx";
import SlidePanel from '../shared/SlidePanel';
import BrandPopup from "./BrandPopup";
import useWindowSize from "../new-service/useWindowSize";
import { Dropdown } from "react-bootstrap";
import { openOnboardingLayer } from "../onboarding/ImportJobContext";
import { ONBOARDING_ENABLED } from "../onboarding/flags";
import Select from "react-select";
import toast from '../shared/Toast';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSensors, useSensor, PointerSensor, TouchSensor } from '@dnd-kit/core';
import ProductCategoryItem from "./ProductCategoryItem";
import ProductDetailPanel from "./ProductDetailPanel";
import ProductEditorPanel from "./ProductEditorPanel";
import CategoryDetailPanel from "../new-service/CategoryDetailPanel";
import CatalogueToolbar from '../shared/CatalogueToolbar';
import MobileDotPopup from "../new-service/MobileDotPopup";
import MessagePopupBody from "../new-service/MessagePopupBody";
import DeleteConfirmationPopup from "../common/DeleteConfirmationPopup";
import { NewServiceSkeletop } from "../newSalon/SkletonLoader";
import '../new-service/service.css';
import '../new-service/newservice.css';
import './product.css';

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    border: state.isFocused ? '1px solid #007EA0' : '1px solid #e0e0e0',
    borderRadius: '10px',
    minHeight: '44px',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(0, 126, 160, 0.08)' : 'none',
    background: '#fff',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      borderColor: state.isFocused ? '#007EA0' : '#ccc',
    },
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#999',
    padding: '8px 10px',
    '&:hover': { color: '#666' },
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '14px',
    color: '#333',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '14px',
    color: '#aaa',
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '12px',
    border: '1px solid #e8e8e8',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.04)',
    overflow: 'hidden',
    zIndex: 20,
    marginTop: '4px',
    marginBottom: '4px',
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '4px',
    maxHeight: '240px',
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '14px',
    padding: '10px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background 0.1s ease',
    backgroundColor: state.isSelected ? '#29282D' : state.isFocused ? '#f0f0f0' : '#fff',
    color: state.isSelected ? '#fff' : '#333',
    '&:active': {
      backgroundColor: state.isSelected ? '#29282D' : '#e8e8e8',
    },
  }),
};

const PRODUCT_SUBTITLES = {
  products: 'Your retail products. Manage stock, pricing, brands, and what sells online or in-store.',
  sales: 'Who is selling, what clients add at checkout, and where the next pound of growth is. Money reports live in Finance.',
  orders: 'Online orders to fulfil: pack, ship, track, and keep customers updated.',
  store: 'Your online product store: publish it, set collection and delivery, and how it presents to clients.',
};

function Product(props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { width } = useWindowSize();

  // State variables
  // Sub-nav tab: 'products' (the existing management page) | 'sales' | 'orders'.
  const [subTab, setSubTab] = useState('products');
  const [showHelp, setShowHelp] = useState(false);

  // Reception mode: nested tabs (Data / Orders / Store) can be hidden per the
  // salon's visibility settings — same source + event the catalogue nav uses.
  const readReceptionSettings = () => {
    try {
      const cached = localStorage.getItem('receptionModeSettings');
      if (cached) return JSON.parse(cached);
    } catch (e) { /* ignore parse errors */ }
    return { enabled: false, hiddenAreas: {} };
  };
  const [receptionSettings, setReceptionSettings] = useState(readReceptionSettings);
  useEffect(() => {
    const onChange = () => setReceptionSettings(readReceptionSettings());
    window.addEventListener('receptionModeSettingsChanged', onChange);
    return () => window.removeEventListener('receptionModeSettingsChanged', onChange);
  }, []);
  const receptionHiddenAreas = (receptionSettings && receptionSettings.enabled && receptionSettings.hiddenAreas) || {};
  const SUBTAB_RECEPTION_KEYS = { sales: 'productData', orders: 'productOrders', store: 'productStore' };
  // Data is salon-wide takings: the `reports` capability, as the server checks.
  const visibleSubTabs = ['products', 'sales', 'orders', 'store']
    .filter((t) => !SUBTAB_RECEPTION_KEYS[t] || !receptionHiddenAreas[SUBTAB_RECEPTION_KEYS[t]])
    .filter((t) => t !== 'sales' || canReports('reports'));
  useEffect(() => {
    if (!visibleSubTabs.includes(subTab)) setSubTab('products');
  }, [visibleSubTabs.join(','), subTab]); // eslint-disable-line react-hooks/exhaustive-deps
  // Count of online orders needing action, for the Orders sub-nav badge.
  const [ordersNeedsCount, setOrdersNeedsCount] = useState(0);
  useEffect(() => {
    try { setOrdersNeedsCount(getOrders().filter((o) => o.fulfilmentStatus === 'unfulfilled').length); } catch (e) { /* ignore */ }
  }, []);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCrossIcon, setSearchCrossIcon] = useState(false);
  const [filters, setFilters] = useState({
    categoryFilter: "",
    brandFilter: "",
    stockStatus: ""
  });
  const [showBrandsPopup, setShowBrandsPopup] = useState(false);
  const [resetFilterPayload, setResetFilterPayload] = useState(null);
  const [products, setProducts] = useState([]);
  const [productList, setProductList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const [filterPopup, setFilterPopup] = useState(false);
  const [settingsPopup, setSettingsPopup] = useState(false);
  const [detailPanelProduct, setDetailPanelProduct] = useState(null);
  const [detailPanelCategory, setDetailPanelCategory] = useState(null);
  const [mobileDotCategory, setMobileDotCategory] = useState(false);
  const [mobileDotProduct, setMobileDotProduct] = useState(false);
  const [deleteProductPopup, setDeleteProductPopup] = useState(false);
  const [deleteCategoryPopup, setDeleteCategoryPopup] = useState(false);
  const [editCategoryPopup, setEditCategoryPopup] = useState(false);
  const [addCategoryPopup, setAddCategoryPopup] = useState(false);
  const [addBrandPopup, setAddBrandPopup] = useState(false);
  const [newProductPanelOpen, setNewProductPanelOpen] = useState(false);
  const [addCategoryName, setAddCategoryName] = useState('');
  const [addCategorySaving, setAddCategorySaving] = useState(false);
  const [addBrandName, setAddBrandName] = useState('');
  const [addBrandSaving, setAddBrandSaving] = useState(false);
  const [editBrandPopup, setEditBrandPopup] = useState(false);
  const [deleteBrandPopup, setDeleteBrandPopup] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState();
  const [selectedProductName, setSelectedProductName] = useState();
  const [selectedCategoryId, setSelectedCategoryId] = useState();
  const [selectedCategoryName, setSelectedCategoryName] = useState();
  const [categoryEditDetail, setCategoryEditDetail] = useState({id: "", name: ""});
  const [brandEditDetail, setBrandEditDetail] = useState({id: "", name: ""});
  const [productEditDetail, setProductEditDetail] = useState({});
  const [editCategoryPayload, setEditCategoryPayload] = useState({});
  const [animate, setAnimate] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [mouseDownPosition, setMouseDownPosition] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState('');

  const PAGE_LIMIT = 50;
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const searchDebounceRef = useRef(null);
  const fetchTokenRef = useRef(0);

  // Initialize currency symbol from user profile
  useEffect(() => {
    if (props?.user?.userInfo?.hairdresser?.currencySymbol) {
      setCurrencySymbol(props.user.userInfo.hairdresser.currencySymbol);
    }
  }, [props?.user?.userInfo?.hairdresser?.currencySymbol]);
  
  const dragThreshold = 10; // pixels to determine if it's a drag

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: dragThreshold,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  // Fetch products and categories on component mount
  useEffect(() => {
    listProducts();
    listAllCategories();
    listAllBrands();
  }, []);

  // Fetch all categories
  const listAllCategories = () => {
    console.log("Fetching all categories");
    
    dispatch(productAction.listProductCategories())
      .then((response) => {
        console.log("Categories response:", response);
        
        let categoryList = null;
        if (response?.data?.responseData?.categories) {
          categoryList = response.data.responseData.categories;
        } else if (response?.data?.responseData?.result) {
          categoryList = response.data.responseData.result;
        }
        
        // Log the displayOrder values for each category
        if (categoryList && categoryList.length > 0) {
          console.log("Category displayOrder values:", categoryList.map(category => ({
            id: category._id || category.categoryId,
            name: category.categoryName,
            displayOrder: category.displayOrder
          })));
        }
        
        if (categoryList) {
          setCategories(categoryList);
        }
      })
      .catch((err) => {
        console.log("Error fetching categories:", err);
      });
  };

  // Fetch all brands
  const listAllBrands = () => {
    console.log("Fetching all brands");
    
    dispatch(productAction.listProductBrands())
      .then((response) => {
        console.log("Brands response:", response);
        
        let brandList = null;
        if (response?.data?.responseData?.brands) {
          brandList = response.data.responseData.brands;
        } else if (response?.data?.responseData?.result) {
          brandList = response.data.responseData.result;
        }
        
        if (brandList) {
          setBrands(brandList);
        }
      })
      .catch((err) => {
        console.log("Error fetching brands:", err);
      });
  };

  // Create products array with all categories (including empty ones)
  useEffect(() => {
    console.log("Creating products array with categories and products");
    console.log("Categories:", categories);
    console.log("ProductList:", productList);
    
    // Create a map of all categories
    const categoryMap = {};
    
    // Add all categories from the API
    if (categories && categories.length > 0) {
      // Sort categories by displayOrder first
      const sortedCategories = [...categories].sort((a, b) => {
        const aOrder = typeof a.displayOrder === 'number' ? a.displayOrder : parseInt(a.displayOrder, 10) || 0;
        const bOrder = typeof b.displayOrder === 'number' ? b.displayOrder : parseInt(b.displayOrder, 10) || 0;
        return aOrder - bOrder;
      });
      
      console.log("Sorted categories by displayOrder:", sortedCategories.map(cat => ({
        id: cat.categoryId || cat._id,
        name: cat.categoryName,
        displayOrder: cat.displayOrder
      })));
      
      sortedCategories.forEach(category => {
        const categoryId = category.categoryId || category._id;
        categoryMap[categoryId] = {
          id: categoryId,
          category: category.categoryName,
          isOpen: true,
          items: [],
          displayOrder: typeof category.displayOrder === 'number' ?
            category.displayOrder : parseInt(category.displayOrder, 10) || 0
        };
      });
    }
    
    // Add "Uncategorized" category
    categoryMap["uncategorized"] = {
      id: "uncategorized",
      category: "Uncategorized",
      isOpen: true,
      items: [],
      displayOrder: 999999 // Always at the end
    };
    
    // Debug log to see what categories we have
    console.log("Category map before adding products:", Object.keys(categoryMap));
    
    // Add products to their respective categories
    if (productList && productList.length > 0) {
      productList.forEach(product => {
        // Debug log to see product category information
        console.log("Processing product:", product.productName, "Category info:", product.category, "CategoryId:", product.categoryId);
        
        // Determine which category this product belongs to
        let categoryId = "uncategorized";
        
        // First try to get categoryId directly from the product
        if (product.categoryId) {
          categoryId = product.categoryId;
          console.log("Using direct categoryId:", categoryId);
        }
        // Then try to get it from the category object
        else if (product.category) {
          if (typeof product.category === 'object') {
            if (product.category._id) {
              categoryId = product.category._id;
              console.log("Using category._id:", categoryId);
            } else if (product.category.categoryId) {
              categoryId = product.category.categoryId;
              console.log("Using category.categoryId:", categoryId);
            }
          } else if (typeof product.category === 'string') {
            categoryId = product.category;
            console.log("Using category string:", categoryId);
          }
        }
        
        // If the category doesn't exist in our map, add it to uncategorized
        if (!categoryMap[categoryId]) {
          console.log("Category not found in map, using uncategorized:", categoryId);
          categoryId = "uncategorized";
        }
        
        // Add the product to its category
        console.log("Adding product to category:", categoryId);
        console.log("Product displayOrder:", product.displayOrder);
        
        // Ensure displayOrder is a number
        const displayOrder = typeof product.displayOrder === 'number' ?
          product.displayOrder : parseInt(product.displayOrder, 10) || 0;
        
        categoryMap[categoryId].items.push({
          id: product._id,
          productName: product.productName,
          retailPrice: product.retailPrice,
          stockPrice: product.stockPrice,
          inStock: product.inStock || 0,
          commissionEnable: product.commissionEnable,
          // Add new fields
          images: product.images || [],
          categoryId: product.categoryId,
          brandId: product.brandId,
          category: product.category,
          brand: product.brand,
          barcode: product.barcode || "",
          sku: product.sku || "",
          trackStock: product.trackStock || 0,
          sellOnline: product.sellOnline || 0,
          description: product.description || "",
          quantity: product.quantity || 0,
          // Retail variants — the editor panel reads these; omitting them here
          // makes a saved variant product look variant-less on reopen.
          options: product.options || [],
          variants: product.variants || [],
          weight: product.weight || 0,
          weightUnit: product.weightUnit,
          displayOrder: displayOrder
        });
      });
    }
    
    // Sort products within each category by displayOrder
    Object.values(categoryMap).forEach(category => {
      // Log the items before sorting for debugging
      console.log(`Before sorting category ${category.category}:`,
        category.items.map(item => ({
          id: item.id,
          name: item.productName,
          displayOrder: item.displayOrder
        }))
      );
      
      category.items.sort((a, b) => {
        // Sort by displayOrder if available, otherwise fall back to name
        if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
          // Make sure displayOrder is treated as a number
          const aOrder = typeof a.displayOrder === 'number' ? a.displayOrder : parseInt(a.displayOrder, 10) || 0;
          const bOrder = typeof b.displayOrder === 'number' ? b.displayOrder : parseInt(b.displayOrder, 10) || 0;
          return aOrder - bOrder;
        }
        
        return a.productName.localeCompare(b.productName);
      });
      
      // Log the items after sorting for debugging
      console.log(`After sorting category ${category.category}:`,
        category.items.map(item => ({
          id: item.id,
          name: item.productName,
          displayOrder: item.displayOrder
        }))
      );
    });
    
    // Convert the map to an array and sort to ensure consistent order
    let mappedProducts = Object.values(categoryMap);
    
    // Filter out categories with no items if any filter is applied or if there's a search query
    if (filters.categoryFilter || filters.brandFilter || filters.stockStatus || searchQuery) {
      mappedProducts = mappedProducts.filter(category => category.items.length > 0);
    }
    
    // Sort categories by displayOrder
    mappedProducts.sort((a, b) => {
      if (a.id === "uncategorized") return 1;
      if (b.id === "uncategorized") return -1;
      
      return a.displayOrder - b.displayOrder;
    });
    
    // Log the mapped products for debugging
    console.log("Final mapped products:", mappedProducts.map(category => ({
      id: category.id,
      name: category.category,
      displayOrder: category.displayOrder,
      itemCount: category.items.length
    })));
    
    // Set the products state
    setProducts(mappedProducts);
    
    // Update loader state
    if (productList.length === 0 && categories.length === 0) {
      setIsLoader(true);
    } else {
      setIsLoader(false);
    }
  }, [productList, categories, filters]);

  // Animation effect for popups
  useEffect(() => {
    if (filterPopup || settingsPopup || mobileDotCategory) {
      setAnimate(true);
    }
  }, [filterPopup, settingsPopup, mobileDotCategory]);

  // Infinite scroll — load next page when near bottom of window.
  useEffect(() => {
    const onScroll = () => {
      if (!hasMore || isLoadingMore || isLoader) return;
      const threshold = 400;
      const scrolled = window.innerHeight + window.scrollY;
      if (scrolled >= document.documentElement.scrollHeight - threshold) {
        loadMoreProducts();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, hasMore, isLoadingMore, isLoader, searchQuery, filters]);

  // Add body class for popups
  useEffect(() => {
    if (filterPopup || settingsPopup || deleteProductPopup || deleteCategoryPopup) {
      document.body.classList.add('filter_modal_body');
    }
    
    return () => {
      document.body.classList.remove('filter_modal_body');
    };
  }, [filterPopup, settingsPopup, deleteProductPopup, deleteCategoryPopup]);

  // Refetch from page 0 whenever filters/search are reset via resetFilterPayload
  useEffect(() => {
    if (resetFilterPayload) {
      listProducts({ search: searchQuery, filters });
      setResetFilterPayload(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetFilterPayload]);

  // Centralized product fetcher with pagination + append support.
  // BE handles search/filter; FE just paginates and appends.
  const fetchProductPage = (opts = {}) => {
    const fetchPage = opts.page ?? 0;
    const append = opts.append ?? false;
    const searchVal = opts.search ?? searchQuery;
    const f = opts.filters ?? filters;
    // Silent refresh: keep the current list on screen while re-fetching (no
    // full-page skeleton flash). Used after an in-place save/edit.
    const silent = opts.silent ?? false;

    const requestData = {
      page: fetchPage,
      limit: PAGE_LIMIT,
      search: searchVal || '',
      categoryId: f.categoryFilter || '',
      brandId: f.brandFilter || '',
      stockStatus: f.stockStatus || ''
    };

    // Cancel any in-flight earlier fetch by bumping the token.
    const myToken = ++fetchTokenRef.current;

    if (append) setIsLoadingMore(true); else if (!silent) setIsLoader(true);

    return dispatch(productAction.listProduct(requestData))
      .then((response) => {
        if (myToken !== fetchTokenRef.current) return; // stale
        let responseList = response?.data?.responseData?.result || [];
        responseList = responseList.map(product => ({
          ...product,
          displayOrder: typeof product.displayOrder === 'number'
            ? product.displayOrder
            : parseInt(product.displayOrder, 10) || 0
        }));

        setHasMore(responseList.length === PAGE_LIMIT);
        setPage(fetchPage);

        if (append) {
          setProductList(prev => [...(prev || []), ...responseList]);
        } else {
          setProductList(responseList);
        }
        return responseList;
      })
      .catch((err) => {
        console.log("Error fetching products:", err);
      })
      .finally(() => {
        if (myToken !== fetchTokenRef.current) return;
        if (append) setIsLoadingMore(false); else if (!silent) setIsLoader(false);
      });
  };

  // Fetch products from API (page 0, replace mode)
  const listProducts = (opts = {}) => fetchProductPage({ page: 0, append: false, ...opts });

  // Load next page and append
  const loadMoreProducts = () => {
    if (!hasMore || isLoadingMore || isLoader) return;
    fetchProductPage({ page: page + 1, append: true });
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value) {
      setSearchCrossIcon(true);
    } else {
      setSearchCrossIcon(false);
    }
    
    // Update filters with new search query
    const updatedFilters = {
      ...filters,
      searchQuery: value
    };
    
    // Update filters state
    setFilters(updatedFilters);
    
    // Debounce search and reset to page 0 — BE handles search filtering.
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      listProducts({ search: value, filters });
    }, 300);
  };

  // The deep product money/commission reporting lives in Finance (real, API-backed).
  // Full navigation (not history.push) so the Finance route-guard resolves the
  // same way it does on a direct load — an in-app push gets bounced by the guard.
  const viewFinance = () => { window.location.href = '/finance?tab=transactions&type=product'; };

  // Clear search input
  const handleResetSearchChange = () => {
    setSearchQuery("");
    setSearchCrossIcon(false);
    const updatedFilters = { ...filters, searchQuery: "" };
    setFilters(updatedFilters);
    setResetFilterPayload(updatedFilters);
    listProducts({ search: "", filters });
  };

  // Handle paste in search input
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');

    if (pastedData !== '') {
      setSearchCrossIcon(true);
      setSearchQuery(pastedData);
      const updatedFilters = { ...filters, searchQuery: pastedData };
      setFilters(updatedFilters);
      setResetFilterPayload(updatedFilters);
      listProducts({ search: pastedData, filters });
    }
  };

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    
    if (!active.data.current?.type) {
      if (products?.find(cat => cat.id === active.id)) {
        setIsDragging(true);
        const allCollapsed = products?.reduce((acc, cat) => {
          acc[cat.id] = true;
          return acc;
        }, {});
        setCollapsedCategories(allCollapsed);
      }
    }
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;

    if (active.data.current?.type === 'item') {
      console.log("Dragging product item:", active.id, "over:", over.id);
      
      setProducts(prevCategories => {
        const activeCategory = active.data.current.category;
        console.log("Active category:", activeCategory);
        
        const category = prevCategories.find(cat => cat.id === activeCategory);
        
        if (!category) {
          console.log("Category not found:", activeCategory);
          return prevCategories;
        }

        const oldIndex = category.items.findIndex(item => item.id === active.id);
        const newIndex = category.items.findIndex(item => item.id === over.id);

        console.log("Moving product from index", oldIndex, "to", newIndex);
        
        if (oldIndex === -1 || newIndex === -1) {
          console.log("Invalid indices:", oldIndex, newIndex);
          return prevCategories;
        }

        const updatedCategories = prevCategories.map(cat => {
          if (cat.id === activeCategory) {
            // Create a new array with the items in the new order
            const newItems = arrayMove([...cat.items], oldIndex, newIndex);
            
            // Update displayOrder values to match the new order
            newItems.forEach((item, index) => {
              item.displayOrder = index;
            });
            
            console.log("New items order:", newItems.map(item => ({
              id: item.id,
              name: item.productName,
              displayOrder: item.displayOrder
            })));
            
            const updatedProducts = {
              ...cat,
              items: newItems
            };
            
            // Call the API to update the order in the backend
            handleProductOrderChange(updatedProducts);
            
            return updatedProducts;
          }
          return cat;
        });

        // Scroll to the dragged product item
        setTimeout(() => {
          const element = document.getElementById(active.id);
          if (element) scrollToElement(element);
        }, 100);

        return updatedCategories;
      });
    } else {
      console.log("Dragging category:", active.id, "over:", over.id);
      
      if (active.id !== over.id) {
        setProducts(prevCategories => {
          const oldIndex = prevCategories.findIndex(cat => cat.id === active.id);
          const newIndex = prevCategories.findIndex(cat => cat.id === over.id);
          
          console.log("Moving category from index", oldIndex, "to", newIndex);
          
          const newArray = arrayMove(prevCategories, oldIndex, newIndex);
          
          // Update displayOrder values to match the new order
          newArray.forEach((category, index) => {
            category.displayOrder = index;
          });
          
          console.log("New category order:", newArray.map(category => ({
            id: category.id,
            name: category.category,
            displayOrder: category.displayOrder
          })));
          
          // Call the API to update the order in the backend
          handleCategoryOrderChange(newArray);

          // Scroll logic
          setTimeout(() => {
            const element = document.getElementById(active.id);
            if (element) {
              const headerHeight = -60;
              const scrollPosition = element.offsetTop - headerHeight - 0;
              window.scrollTo({
                top: Math.max(0, scrollPosition),
                behavior: 'smooth'
              });
            }
          }, 100);

          return newArray;
        });
       
        if (isDragging) {
          setCollapsedCategories({});
        }
      }
    }
    
    setIsDragging(false);
    setMouseDownPosition(null);
  };

  // Handle toggle all categories
  const handleToggleAll = () => {
    const allCollapsed = products?.every(cat => collapsedCategories[cat.id]);
    
    if (allCollapsed) {
      setCollapsedCategories({});
    } else {
      const allClosed = products?.reduce((acc, cat) => {
        acc[cat.id] = true;
        return acc;
      }, {});
      setCollapsedCategories(allClosed);
    }
  };

  // Handle category order change
  const handleCategoryOrderChange = (newProducts) => {
    console.log("Category order changed:", newProducts);
    
    // Extract category IDs in the new order
    const categoryIds = newProducts.map(category => category.id);
    
    console.log("Category IDs in new order:", categoryIds);
    console.log("Categories with displayOrder:", newProducts.map(category => ({
      id: category.id,
      name: category.category,
      displayOrder: category.displayOrder
    })));
    
    // Create payload for the API call
    const payload = {
      categoryId: categoryIds.join(',')
    };
    
    console.log("Sending payload to update category order:", payload);
    
    // Make API call to update category order
    dispatch(productAction.updateProductCategoryOrder(payload))
      .then((response) => {
        console.log("Category order updated successfully:", response);
        toast.success("Category order updated");
        
        // Don't refresh data from server after successful reordering
        // The local state is already updated with the new order
      })
      .catch((error) => {
        console.error("Error updating category order:", error);
        toast.error("Failed to update category order");
      });
  };

  // Handle product order change
  const handleProductOrderChange = (updatedProducts) => {
    console.log("Product order changed:", updatedProducts);
    
    // Extract product IDs in the new order
    const productIds = updatedProducts.items.map(item => item.id);
    
    console.log("Product IDs in new order:", productIds);
    console.log("Product items with displayOrder:", updatedProducts.items.map(item => ({
      id: item.id,
      name: item.productName,
      displayOrder: item.displayOrder
    })));
    
    // Create payload for the API call
    const payload = {
      productId: productIds.join(','),
      categoryId: updatedProducts.id
    };
    
    console.log("Sending payload to update product order:", payload);
    
    // Make API call to update product order
    dispatch(productAction.updateProductOrder(payload))
      .then((response) => {
        console.log("Product order updated successfully:", response);
        toast.success("Product order updated");
        
        // Log the response data to see what's returned
        if (response?.data?.responseData) {
          console.log("Product order update response data:", response.data.responseData);
        }
        
        // The local state is already updated with the new order in handleDragEnd
        // No need to refresh data from server after successful reordering
      })
      .catch((error) => {
        console.error("Error updating product order:", error);
        toast.error("Failed to update product order");
      });
  };

  // Handle add product button click
  const handleAddProductClick = () => {
    history.push("/product/addProduct");
  };

  // Handle filter button click
  const filterClick = () => {
    setFilterPopup(true);
  };
  
  // Handle reset filters
  const handleResetFilters = (filterName) => {
    const resetFilters = { ...filters };
    if (filterName === 'categoryFilter') resetFilters.categoryFilter = "";
    else if (filterName === 'brandFilter') resetFilters.brandFilter = "";
    else if (filterName === 'stockStatus') resetFilters.stockStatus = "";

    setFilters(resetFilters);
    setResetFilterPayload(resetFilters);
    listProducts({ search: resetFilters.searchQuery || searchQuery, filters: resetFilters });
  };

  const handleClearAllFilters = () => {
    const resetFilters = { ...filters, categoryFilter: "", brandFilter: "", stockStatus: "" };
    setFilters(resetFilters);
    setResetFilterPayload(resetFilters);
    listProducts({ search: resetFilters.searchQuery || searchQuery, filters: resetFilters });
  };

  // Handle apply filters
  const handleApplyFilters = (selectedFilters) => {
    const updatedFilters = { ...filters, ...selectedFilters };
    setFilters(updatedFilters);
    setFilterPopup(false);
    listProducts({ search: updatedFilters.searchQuery || searchQuery, filters: updatedFilters });
  };

  // Handle settings button click
  const settingClick = () => {
    setSettingsPopup(true);
  };

  // Handle close for popups
  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      setFilterPopup(false);
      setSettingsPopup(false);
      setMobileDotCategory(false);
      setMobileDotProduct(false);
    }, 300);
  };

  // Mobile category dot popup
  const mobileDotCategoryPopup = (categoryId, categoryName) => {
    setMobileDotCategory(true);
    setCategoryEditDetail({ id: categoryId, name: categoryName });
  };

  // Mobile product dot popup
  const mobileDotProductPopup = (product) => {
    setProductEditDetail(product);
    setMobileDotProduct(true);
  };

  // Edit category
  const editCategory = (categoryId, categoryName) => {
    setEditCategoryPopup(true);
    let payload = {
      "categoryId": categoryId,
      "categoryName": categoryName
    };
    setEditCategoryPayload(payload);
  };

  // Handle edit category
  const handleEditCategory = (categoryName) => {
    if (!editCategoryPayload.categoryId) {
      toast.error("Category ID is required");
      return;
    }

    if (!categoryName) {
      toast.error("Category name is required");
      return;
    }

    let payload = {
      "categoryId": editCategoryPayload.categoryId,
      "categoryName": categoryName
    };
    
    dispatch(productAction.updateProductCategory(payload))
      .then((response) => {
        if (response?.data?.statusCode === 1) {
          toast.success("Category updated successfully");
          setEditCategoryPopup(false);
          listProducts({ silent: true }); // Refresh the product list
          listAllCategories(); // Refresh the categories list
        } else {
          toast.error(response?.data?.error?.responseMessage || "Failed to update category");
        }
      })
      .catch((error) => {
        console.error("Error updating category:", error);
        toast.error("Failed to update category");
      });
  };

  // Handle edit brand
  const handleEditBrand = (brandName) => {
    if (!brandEditDetail.id) {
      toast.error("Brand ID is required");
      return;
    }

    if (!brandName) {
      toast.error("Brand name is required");
      return;
    }

    const payload = {
      brandId: brandEditDetail.id,
      brandName: brandName
    };
    
    dispatch(productAction.updateProductBrand(payload))
      .then((response) => {
        if (response?.data?.statusCode === 1) {
          toast.success("Brand updated successfully");
          setEditBrandPopup(false);
          listProducts({ silent: true }); // Refresh the product list
        } else {
          toast.error(response?.data?.error?.responseMessage || "Failed to update brand");
        }
      })
      .catch((error) => {
        console.error("Error updating brand:", error);
        toast.error("Failed to update brand");
      });
  };

  // Delete category
  const deleteCategory = (categoryId, categoryName) => {
    setSelectedCategoryId(categoryId);
    setSelectedCategoryName(categoryName);
    setDeleteCategoryPopup(true);
  };

  // Delete product
  const deleteProduct = (productId, productName) => {
    setSelectedProductId(productId);
    setSelectedProductName(productName);
    setDeleteProductPopup(true);
  };

  // Message templates for popups
  const deleteCategoryMsg = {
    heading: `Delete Category - ${selectedCategoryName}`,
    description: "Are you sure you want to delete this category? This will also delete all products within this category. This action cannot be undone.",
    btntext1: "Cancel",
    btntext2: "Delete",
  };

  const deleteProductMsg = {
    heading: `Delete Product - ${selectedProductName}`,
    description: "Are you sure you want to delete this product? This action cannot be undone.",
    btntext1: "Cancel",
    btntext2: "Delete",
  };

  return (
    <div className="h_n _new__product catalogue-no-header">
      <div className="body_container cm_bg_white">
        <div className="catalogue-page-wrapper">
          <CatalogueTopNav>
            <Dropdown className="catalogue-add-dropdown">
              <Dropdown.Toggle id="catalogue-add-products">
                <span>Add</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setNewProductPanelOpen(true)}>New Product</Dropdown.Item>
                <Dropdown.Item onClick={() => { setAddCategoryName(''); setAddCategoryPopup(true); }}>New Category</Dropdown.Item>
                <Dropdown.Item onClick={() => { setAddBrandName(''); setAddBrandPopup(true); }}>New Brand</Dropdown.Item>
                {ONBOARDING_ENABLED && canImport() && <Dropdown.Item onClick={() => openOnboardingLayer({ mode: 'import' })}>Import from a file</Dropdown.Item>}
              </Dropdown.Menu>
            </Dropdown>
          </CatalogueTopNav>

          {/* Page header + sub-nav (Products / Sales / Orders), matching
              Vouchers V2. The existing product management is the Products tab. */}
          <div className="pv2-head">
            <div className="pv2-title-row">
              <h1 className="pv2-title">Products</h1>
              <button type="button" className="pv2-help-btn" onClick={() => setShowHelp(true)} aria-label="How products work" title="How products work">?</button>
            </div>
            <p className="pv2-sub">{PRODUCT_SUBTITLES[subTab]}</p>
          </div>
          {showHelp && <ProductHelp onClose={() => setShowHelp(false)} />}
          <ProductSubNav
            active={subTab}
            onChange={setSubTab}
            visible={visibleSubTabs}
            counts={{
              products: (productList && productList.length) || undefined,
              orders: ordersNeedsCount || undefined,
            }}
          />

          {subTab === 'products' && (
          <div className="catalogue-content-container">
            <div className="catalogue-content-wrapper">
                <CatalogueToolbar
                    searchValue={searchQuery}
                    onSearchChange={handleSearchChange}
                    onSearchPaste={handlePaste}
                    onSearchClear={handleResetSearchChange}
                    showClear={searchCrossIcon}
                    placeholder="Search products..."
                    onFilterClick={filterClick}
                />
                
                {(filters?.categoryFilter || filters?.brandFilter || filters?.stockStatus) && (
                  <div className="applied_filter_container">
                    {filters.categoryFilter && (
                      <span>
                        {/* Find and display the category name instead of ID */}
                        {categories.find(cat =>
                          (cat._id || cat.categoryId) === filters.categoryFilter
                        )?.categoryName || 'Category'}
                        <i className="_cross" onClick={() => handleResetFilters('categoryFilter')}><RxCross2 /></i>
                      </span>
                    )}
                    {filters.brandFilter && (
                      <span>
                        {/* Find and display the brand name instead of ID */}
                        {brands.find(brand =>
                          (brand._id || brand.brandId) === filters.brandFilter
                        )?.brandName || 'Brand'}
                        <i className="_cross" onClick={() => handleResetFilters('brandFilter')}><RxCross2 /></i>
                      </span>
                    )}
                    {filters.stockStatus && (
                      <span>
                        {filters.stockStatus === "inStock" ? 'In Stock' : 'Out of Stock'}
                        <i className="_cross" onClick={() => handleResetFilters('stockStatus')}><RxCross2 /></i>
                      </span>
                    )}
                    <button type="button" className="applied_filter_clear" onClick={handleClearAllFilters}>Clear all</button>
                  </div>
                )}
                
                {isLoader ?
                  <NewServiceSkeletop />
                  :
                  <>
                    {products.length === 0 ? 
                      <p className="text-center mt-5">No Products Found</p> 
                      :
                      <div className="App">
                        <DndContext 
                          collisionDetection={closestCenter} 
                          onDragEnd={handleDragEnd}
                          onDragStart={handleDragStart}
                          modifiers={[restrictToVerticalAxis]}
                          sensors={sensors}
                        >
                          <div className="app">
                            <div className="_dragable_component pt-4">
                              <SortableContext 
                                items={products.map(category => category.id)} 
                                strategy={verticalListSortingStrategy}
                              >
                                {products.map((category) => (
                                  <ProductCategoryItem 
                                    key={category.id}
                                    category={category}
                                    isCollapsed={collapsedCategories[category.id]}
                                    onToggleAll={handleToggleAll}
                                    isDragging={isDragging}
                                    mouseDownPosition={mouseDownPosition}
                                    setMouseDownPosition={setMouseDownPosition}
                                    dragThreshold={dragThreshold}
                                    onOpenCategoryDetail={(cat) => setDetailPanelCategory(cat)}
                                    mobileDotProductPopup={mobileDotProductPopup}
                                    deleteProduct={deleteProduct}
                                    currencySymbol={currencySymbol}
                                    onOpenProductDetail={(product) => setDetailPanelProduct(product)}
                                  />
                                ))}
                              </SortableContext>
                            </div>
                          </div>
                        </DndContext>
                        {isLoadingMore && (
                          <div className="text-center py-3" style={{ color: '#888', fontSize: '13px' }}>
                            Loading more…
                          </div>
                        )}
                      </div>
                    }
                  </>
                }
            </div>
          </div>
          )}

          {subTab === 'sales' && (
            <div className="catalogue-content-container">
              <div className="catalogue-content-wrapper">
                <ProductData currency="£" onOpenFinance={viewFinance} products={productList} />
              </div>
            </div>
          )}

          {subTab === 'orders' && (
            <div className="catalogue-content-container">
              <div className="catalogue-content-wrapper">
                <ProductOrders currency="£" onCountChange={setOrdersNeedsCount} />
              </div>
            </div>
          )}

          {subTab === 'store' && (
            <div className="catalogue-content-container">
              <div className="catalogue-content-wrapper">
                <ProductStoreSettings currency="£" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filter panel */}
      {filterPopup && (
        <SlidePanel
          title="Filters"
          onClose={() => setFilterPopup(false)}
          width={380}
          compact
          footer={
            <>
              <button className="bdp-btn bdp-btn--secondary" onClick={() => setFilterPopup(false)}>Cancel</button>
              <button className="bdp-btn bdp-btn--primary" onClick={() => { handleApplyFilters(filters); setFilterPopup(false); }}>Apply</button>
            </>
          }
        >
          <div className="filters-popup">
            <div className="filter-group">
              <label>Category</label>
              <Select
                menuPlacement="bottom"
                classNamePrefix="my-select"
                value={filters.categoryFilter ? {
                  value: filters.categoryFilter,
                  label: categories.find(cat => (cat._id || cat.categoryId) === filters.categoryFilter)?.categoryName || 'All Categories'
                } : null}
                onChange={(selected) => setFilters({...filters, categoryFilter: selected ? selected.value : ""})}
                options={[
                  { value: "", label: "All Categories" },
                  ...categories.map(category => ({
                    value: category._id || category.categoryId,
                    label: category.categoryName
                  }))
                ]}
                styles={selectStyles}
                placeholder="All Categories"
                isSearchable={false}
              />
            </div>

            <div className="filter-group">
              <label>Brand</label>
              <Select
                menuPlacement="bottom"
                classNamePrefix="my-select"
                value={filters.brandFilter ? {
                  value: filters.brandFilter,
                  label: brands.find(brand => (brand._id || brand.brandId) === filters.brandFilter)?.brandName || 'All Brands'
                } : null}
                onChange={(selected) => setFilters({...filters, brandFilter: selected ? selected.value : ""})}
                options={[
                  { value: "", label: "All Brands" },
                  ...brands.map(brand => ({
                    value: brand._id || brand.brandId,
                    label: brand.brandName
                  }))
                ]}
                styles={selectStyles}
                placeholder="All Brands"
                isSearchable={false}
              />
            </div>

            <div className="filter-group">
              <label>Stock Status</label>
              <Select
                menuPlacement="bottom"
                classNamePrefix="my-select"
                value={filters.stockStatus ? {
                  value: filters.stockStatus,
                  label: filters.stockStatus === "inStock" ? "In Stock" : "Out of Stock"
                } : null}
                onChange={(selected) => setFilters({...filters, stockStatus: selected ? selected.value : ""})}
                options={[
                  { value: "", label: "All" },
                  { value: "inStock", label: "In Stock" },
                  { value: "outOfStock", label: "Out of Stock" }
                ]}
                styles={selectStyles}
                placeholder="All"
                isSearchable={false}
              />
            </div>
          </div>
        </SlidePanel>
      )}

      {detailPanelProduct && (
        <ProductDetailPanel
          product={detailPanelProduct}
          onClose={() => setDetailPanelProduct(null)}
          onDelete={(id, name) => {
            setDetailPanelProduct(null);
            deleteProduct(id, name);
          }}
          onSaved={() => {
            // Refresh the open panel's product too — otherwise the detail/edit
            // views keep showing the stale pre-save object until it's reopened
            // from the list.
            listProducts({ silent: true }).then((rows) => {
              if (!Array.isArray(rows)) return;
              setDetailPanelProduct((current) => {
                if (!current) return current;
                // The panel may hold a grouped-row object (id) or a raw list
                // doc (_id) — match on either.
                const currentId = current._id || current.id;
                const fresh = rows.find((p) => (p._id || p.id) === currentId);
                return fresh || current;
              });
            });
          }}
          currency={currencySymbol}
        />
      )}

      {detailPanelCategory && (
        <CategoryDetailPanel
          category={detailPanelCategory}
          onClose={() => setDetailPanelCategory(null)}
          onSave={(id, newName) => {
            dispatch(productAction.updateProductCategory({ categoryId: id, categoryName: newName }))
              .then((response) => {
                if (response?.data?.statusCode === 1) {
                  toast.success('Category updated successfully');
                } else {
                  toast.error(response?.data?.error?.responseMessage || 'Failed to update category');
                }
                listProducts({ silent: true });
                listAllCategories();
                setDetailPanelCategory(null);
              })
              .catch((error) => {
                console.error('Error updating category:', error);
                toast.error('Failed to update category');
              });
          }}
          onDelete={(id, name) => {
            setDetailPanelCategory(null);
            deleteCategory(id, name);
          }}
          itemCount={detailPanelCategory.itemCount || 0}
          itemLabel="products"
        />
      )}

      {/* Edit Category popup */}
      <MessagePopupBody
        blurClass="blure_bg"
        centered="centered"
        show={editCategoryPopup}
        onHide={() => setEditCategoryPopup(false)}
        backdropClassName="_light_bg"
        size="lg"
        className='_service__popup _no_animation'
        popupType="edit"
        message="Category"
        initialValue={editCategoryPayload.categoryName}
        submitFunction={handleEditCategory}
      />

      {/* Add Category — matches the Edit Category SlidePanel style. */}
      {addCategoryPopup && (
        <SlidePanel
          onClose={() => { setAddCategoryPopup(false); setAddCategoryName(''); }}
          className="sdp-panel"
          width={380}
          compact
        >
          <div className="sdp-header">
            <div className="sdp-header-info">
              <h2 className="sdp-header-name">New Category</h2>
            </div>
            <button className="beu-icon-btn" onClick={() => { setAddCategoryPopup(false); setAddCategoryName(''); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="sdp-body">
            <div className="sdp-section">
              <p className="beu-section-label">Category Name</p>
              <input
                type="text"
                className="cdp-name-input"
                autoFocus
                value={addCategoryName}
                onChange={(e) => setAddCategoryName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitAddCategory(); }}
                placeholder="Enter category name"
              />
            </div>
          </div>

          <div className="sdp-footer">
            <button
              type="button"
              className="sdp-btn sdp-btn--neutral"
              onClick={() => { setAddCategoryPopup(false); setAddCategoryName(''); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="sdp-btn sdp-btn--primary"
              onClick={submitAddCategory}
              disabled={!addCategoryName.trim() || addCategorySaving}
            >
              {addCategorySaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </SlidePanel>
      )}

      {/* Add Brand — matches the Edit Category SlidePanel style. */}
      {addBrandPopup && (
        <SlidePanel
          onClose={() => { setAddBrandPopup(false); setAddBrandName(''); }}
          className="sdp-panel"
          width={380}
          compact
        >
          <div className="sdp-header">
            <div className="sdp-header-info">
              <h2 className="sdp-header-name">New Brand</h2>
            </div>
            <button className="beu-icon-btn" onClick={() => { setAddBrandPopup(false); setAddBrandName(''); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="sdp-body">
            <div className="sdp-section">
              <p className="beu-section-label">Brand Name</p>
              <input
                type="text"
                className="cdp-name-input"
                autoFocus
                value={addBrandName}
                onChange={(e) => setAddBrandName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitAddBrand(); }}
                placeholder="Enter brand name"
              />
            </div>
          </div>

          <div className="sdp-footer">
            <button
              type="button"
              className="sdp-btn sdp-btn--neutral"
              onClick={() => { setAddBrandPopup(false); setAddBrandName(''); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="sdp-btn sdp-btn--primary"
              onClick={submitAddBrand}
              disabled={!addBrandName.trim() || addBrandSaving}
            >
              {addBrandSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </SlidePanel>
      )}

      {/* New Product — slide-in editor panel, replaces /product/addProduct. */}
      {newProductPanelOpen && (
        <ProductEditorPanel
          currency={currencySymbol || '£'}
          onClose={() => setNewProductPanelOpen(false)}
          onSaved={() => {
            setNewProductPanelOpen(false);
            listProducts({ silent: true });
          }}
        />
      )}

      {/* Edit Brand popup */}
      <MessagePopupBody
        blurClass="blure_bg"
        centered="centered"
        show={editBrandPopup}
        onHide={() => setEditBrandPopup(false)}
        backdropClassName="_light_bg"
        size="lg"
        className='_service__popup _no_animation'
        popupType="edit"
        message="Brand"
        initialValue={brandEditDetail.name}
        submitFunction={handleEditBrand}
      />

      {/* Delete Brand popup */}
      <DeleteConfirmationPopup
        show={deleteBrandPopup}
        onHide={() => setDeleteBrandPopup(false)}
        itemType="brand"
        itemId={brandEditDetail.id}
        itemName={brandEditDetail.name}
        refreshList={listProducts}
      />

      {/* Delete Category popup */}
      <DeleteConfirmationPopup
        show={deleteCategoryPopup}
        onHide={() => setDeleteCategoryPopup(false)}
        itemType="productCategory"
        itemId={selectedCategoryId}
        itemName={selectedCategoryName}
        refreshList={listProducts}
        refreshCategories={listAllCategories}
      />

      {/* Delete product popup */}
      <DeleteConfirmationPopup
        show={deleteProductPopup}
        onHide={() => setDeleteProductPopup(false)}
        itemType="product"
        itemId={selectedProductId}
        itemName={selectedProductName}
        refreshList={listProducts}
      />

      {/* Mobile dot category popup */}
      <MobileDotPopup 
        blurClass="blure_bg"
        centered="centered"
        show={mobileDotCategory} 
        onHide={handleClose}
        backdropClassName="_light_bg"
        dialogClassName={`bottom-modal ${animate ? 'slide-in' : 'slide-out'}`}
        size="lg" 
        className='_product__mobile_popup _no_animation'
        type="Category"
        details={categoryEditDetail}
        editOnClickPopup={() => editCategory(categoryEditDetail.id, categoryEditDetail.name)}
        deleteOnClickPopup={() => deleteCategory(categoryEditDetail?.id, categoryEditDetail?.name)}
      /> 

      {/* Mobile dot product popup */}
      <MobileDotPopup 
        blurClass="blure_bg"
        centered="centered"
        show={mobileDotProduct} 
        onHide={handleClose}
        backdropClassName="_light_bg"
        dialogClassName={`bottom-modal ${animate ? 'slide-in' : 'slide-out'}`}
        size="lg" 
        className='_product__mobile_popup _no_animation'
        details={productEditDetail}
        type="Product"
        deleteOnClickPopup={() => deleteProduct(productEditDetail?.id, productEditDetail?.productName)}
      />

      {/* Brands Popup */}
      <BrandPopup
        show={showBrandsPopup}
        onHide={(e) => {
          setShowBrandsPopup(false);
          // When the BrandPopup is closed via the Add Brand button,
          // we need to show the Add Brand popup
          if (e && e.target) {
            if (e.target.innerText === "Add Brand") {
              setAddBrandPopup(true);
            } else if (e.target.innerText === "Edit Brand" && e.target.brandData) {
              // Set the brand data for editing
              setBrandEditDetail({
                id: e.target.brandData.brandId || e.target.brandData._id,
                name: e.target.brandData.brandName
              });
              // Show the edit brand popup
              setEditBrandPopup(true);
            } else if (e.target.innerText === "Delete Brand" && e.target.brandData) {
              // Set the brand data for deleting
              setBrandEditDetail({
                id: e.target.brandData.brandId || e.target.brandData._id,
                name: e.target.brandData.brandName
              });
              // Show the delete brand popup
              setDeleteBrandPopup(true);
            }
          }
        }}
        animate={animate}
      />
    </div>
  );

  // Handle add category
  function handleAddCategory(categoryName) {
    if (!categoryName) {
      toast.error("Category name is required");
      return;
    }

    const payload = {
      categoryName: categoryName
    };

    setAddCategorySaving(true);
    dispatch(productAction.addProductCategory(payload))
      .then((response) => {
        if (response?.data?.statusCode === 1) {
          toast.success("Category added successfully");
          setAddCategoryPopup(false);
          setAddCategoryName('');
          listProducts({ silent: true }); // Refresh the product list
          listAllCategories(); // Refresh the categories list
        } else {
          toast.error(response?.data?.error?.responseMessage || "Failed to add category");
        }
        setAddCategorySaving(false);
      })
      .catch((error) => {
        console.error("Error adding category:", error);
        toast.error("Failed to add category");
        setAddCategorySaving(false);
      });
  }

  function submitAddCategory() {
    const name = addCategoryName.trim();
    if (!name || addCategorySaving) return;
    handleAddCategory(name);
  }

  // Handle add brand
  function handleAddBrand(brandName) {
    if (!brandName) {
      toast.error("Brand name is required");
      return;
    }

    const payload = {
      brandName: brandName
    };

    setAddBrandSaving(true);
    dispatch(productAction.addProductBrand(payload))
      .then((response) => {
        if (response?.data?.statusCode === 1) {
          toast.success("Brand added successfully");
          setAddBrandPopup(false);
          setAddBrandName('');
          listProducts({ silent: true }); // Refresh the product list
        } else {
          toast.error(response?.data?.error?.responseMessage || "Failed to add brand");
        }
        setAddBrandSaving(false);
      })
      .catch((error) => {
        console.error("Error adding brand:", error);
        toast.error("Failed to add brand");
        setAddBrandSaving(false);
      });
  }

  function submitAddBrand() {
    const name = addBrandName.trim();
    if (!name || addBrandSaving) return;
    handleAddBrand(name);
  }

  // Handle delete brand
  function handleDeleteBrand() {
    console.log("Deleting brand:", brandEditDetail);
    if (!brandEditDetail.id) {
      toast.error("Brand ID is required");
      return;
    }

    dispatch(productAction.deleteProductBrand(brandEditDetail.id))
      .then((response) => {
        if (response?.data?.statusCode === 1) {
          toast.success("Brand deleted successfully");
          setDeleteBrandPopup(false);
          listProducts({ silent: true }); // Refresh the product list
        } else {
          toast.error(response?.data?.error?.responseMessage || "Failed to delete brand");
        }
      })
      .catch((error) => {
        console.error("Error deleting brand:", error);
        toast.error("Failed to delete brand");
      });
  }
}

// Helper function for smooth scrolling
const scrollToElement = (element) => {
  const headerHeight = 80;
  const scrollPosition = element.offsetTop - headerHeight - 10;
  window.scrollTo({
    top: Math.max(0, scrollPosition),
    behavior: 'smooth'
  });
};

function mapStateToProps(state) {
  return {
    ...state
  };
}

export default connect(mapStateToProps)(Product);
